import { memo, useEffect, useRef, useCallback, useState } from 'react';
import { XIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { AnnotationMarker } from '../types';

interface MarkerInputProps {
  marker: AnnotationMarker;
  containerRect: DOMRect | null;
  onChange: (description: string) => void;
  onClose: () => void;
  onDelete: () => void;
}

interface InputPosition {
  top: number;
  left: number;
  side: 'left' | 'right';
}

function calculateInputPosition(
  marker: AnnotationMarker,
  containerRect: DOMRect | null,
): InputPosition {
  if (!containerRect) {
    return { top: 0, left: 0, side: 'right' };
  }

  const markerX = (marker.position.x / 100) * containerRect.width;
  const markerY = (marker.position.y / 100) * containerRect.height;

  const inputWidth = 280;
  const inputHeight = 160; // Updated to match actual popup height
  const offset = 50;

  // Determine side based on available space
  const spaceOnRight = containerRect.width - markerX - offset;
  const preferRight = spaceOnRight > inputWidth;

  let left: number;
  let side: 'left' | 'right';

  if (preferRight) {
    left = markerX + offset;
    side = 'right';
  } else {
    left = markerX - inputWidth - offset + 20;
    side = 'left';
  }

  // Ensure left is within bounds
  left = Math.max(10, Math.min(left, containerRect.width - inputWidth - 10));

  // Calculate top position
  // If marker is in bottom 40% of container, position popup ABOVE the marker
  const markerYPercent = marker.position.y;
  let top: number;

  if (markerYPercent > 60) {
    // Position above the marker
    top = markerY - inputHeight - 20;
    top = Math.max(10, top);
  } else {
    // Position at marker level (original behavior)
    top = markerY - 30;
    top = Math.min(top, containerRect.height - inputHeight - 10);
  }

  return { top, left, side };
}

function MarkerInput({
  marker,
  containerRect,
  onChange,
  onClose,
  onDelete,
}: MarkerInputProps) {
  const { t } = useLocale('ai-images');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [localValue, setLocalValue] = useState(marker.description);
  const position = calculateInputPosition(marker, containerRect);

  useEffect(() => {
    setLocalValue(marker.description);
  }, [marker.description]);

  useEffect(() => {
    // Focus input when mounted
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setLocalValue(value);
      onChange(value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <div
      className='absolute z-30 animate-in fade-in slide-in-from-top-2 duration-200'
      style={{
        top: position.top,
        left: position.left,
        width: 280,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Connection line */}
      <svg
        className='absolute pointer-events-none'
        style={{
          width: '100%',
          height: '100%',
          left: position.side === 'right' ? -50 : 'auto',
          right: position.side === 'left' ? -50 : 'auto',
          top: 0,
          overflow: 'visible',
        }}
      >
        <line
          x1={position.side === 'right' ? 0 : 280 + 50}
          y1={30}
          x2={position.side === 'right' ? 50 : 280}
          y2={30}
          stroke='rgb(249, 115, 22)'
          strokeWidth={2}
          strokeDasharray='4 4'
          className='opacity-60'
        />
      </svg>

      {/* Input card */}
      <div className='relative bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden backdrop-blur-sm'>
        {/* Header */}
        <div className='flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-gray-600'>
          <div className='flex items-center gap-2'>
            <span className='w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-semibold flex items-center justify-center'>
              {marker.number}
            </span>
          </div>
          <button
            onClick={onDelete}
            className='p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors'
          >
            <XIcon className='w-4 h-4' />
          </button>
        </div>

        {/* Input */}
        <div className='p-3'>
          <textarea
            ref={inputRef}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={t('Describe what to change here...')}
            className='w-full text-sm text-gray-700 dark:text-gray-200 bg-transparent border-none outline-none resize-none placeholder:text-gray-400 dark:placeholder:text-gray-500'
            rows={2}
          />
        </div>

        {/* Footer with confirm button */}
        <div className='px-3 pb-3 flex justify-end'>
          <button
            onClick={onClose}
            className='px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors'
          >
            {t('Done')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(MarkerInput);
